import React, { useEffect, useMemo } from 'react';
import * as PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Alert } from '@material-ui/lab';
import { Box } from '@material-ui/core';
import Typography from '../../mui/Components/Typography';
import ContractSelect from '../../contracts/components/ContractSelect';
import ContractDate from '../../contracts/components/ContractDate';
import { useFieldFast } from '../../form/hooks';
import { useContract, useContractsByCustomerId } from '../../contracts/contractsSlice';
import { isActiveAgencyContract } from '../../contracts/hooks';
import { selectAgencyById } from '../../agencies/agenciesSlice';
import { useCareNeed } from '../../careNeeds/careNeedsSlice';
import { NO_CARE_NEED_AGENCY_MESSAGE } from '../constants';
import { careNeedDashboardPath } from '../../nav/paths';
import { useCustomer } from '../../customers/customersSlice';
import { showCustomerOnCareNeedDashboard } from '../../kanban/careNeedDashboardFilters';
import { IdPropType } from '../../../proptypes/basic';

const agencyName = (agency) => (agency ? agency.matchCode || agency.name : '');

/**
 * Works out the contracts a new BK-Einsatz of this BK-Bedarf may run on: the active
 * agency contracts (isActiveAgencyContract) of the BK-Bedarf's agency (careNeed.agencyId).
 * No agency yet, no contract - the agency is set by confirming a BK-Vorschlag
 * (docs/plans/care-need-single-agency.md, rule 4).
 */
export const useDeploymentContractCandidates = (customerId, careNeedId) => {
    const [contracts, { initialized: contractsInitialized }] = useContractsByCustomerId(customerId);
    const [careNeed, { initialized: careNeedInitialized }] = useCareNeed(careNeedId);

    const agencyId = (careNeed && careNeed.agencyId) || null;
    const candidates = useMemo(
        () =>
            (contracts || []).filter(
                (contract) =>
                    !!agencyId && isActiveAgencyContract(contract) && contract.agencyId === agencyId
            ),
        [contracts, agencyId]
    );

    return {
        agencyId,
        candidates,
        loading: !contractsInitialized || (!!careNeedId && !careNeedInitialized),
    };
};

const ContractText = ({ contractId }) => {
    const [contract] = useContract(contractId);
    const agency = useSelector((state) => selectAgencyById(state, contract && contract.agencyId));

    if (!contract) return null;
    return (
        <Box>
            <Typography variant="caption" color="textSecondary">
                Vertrag
            </Typography>
            <Typography>
                <strong>{agencyName(agency)}</strong> – Vertrag <ContractDate contract={contract} />
            </Typography>
        </Box>
    );
};

ContractText.propTypes = {
    contractId: IdPropType.isRequired,
};

/**
 * The agency contract of a BK-Einsatz (docs/plans/care-need-single-agency.md, rules 2, 4, 6):
 * - create: no choice - the one active contract of the BK-Bedarf's agency is preselected
 *   and shown as text; only if that agency has several active contracts is there a
 *   (limited) select; with none, creating is blocked and the reason is shown.
 * - edit: the contract is fixed (accounting) and shown as text. Only root may switch to
 *   another active contract of the same agency.
 */
const DeploymentContractField = ({
    name,
    customerId,
    isEdit,
    isRoot,
    disabled,
    onBlockedChange,
    onNavigateAway,
}) => {
    const [{ value: careNeedId }] = useFieldFast('careNeedId');
    const [{ value: contractId }, , { setValue: setContractId }] = useFieldFast(name);
    const [{ value: agencyIdOfDeployment }] = useFieldFast('agencyId');
    const [contracts] = useContractsByCustomerId(customerId);

    const { agencyId, candidates, loading } = useDeploymentContractCandidates(
        customerId,
        careNeedId
    );
    const agency = useSelector((state) => selectAgencyById(state, agencyId));
    const [customer] = useCustomer(customerId);

    // opens the Einsatzboard on this customer (sets its "Filtern nach" search)
    const handleOpenDashboard = () => {
        if (customer && customer.displayNames && customer.displayNames[0]) {
            showCustomerOnCareNeedDashboard(customer.displayNames[0]);
        }
        onNavigateAway();
    };

    // root may switch an existing deployment to another active contract of the same agency
    const rootOptions = useMemo(
        () =>
            isEdit && isRoot
                ? (contracts || []).filter(
                      (contract) =>
                          contract.id === contractId ||
                          (isActiveAgencyContract(contract) &&
                              contract.agencyId === agencyIdOfDeployment)
                  )
                : [],
        [isEdit, isRoot, contracts, contractId, agencyIdOfDeployment]
    );

    const blocked = !isEdit && !loading && candidates.length === 0;
    useEffect(() => {
        onBlockedChange(blocked);
    }, [blocked, onBlockedChange]);

    // create: preselect the only possible contract, drop one that no longer fits
    // (e.g. after switching to another BK-Bedarf)
    useEffect(() => {
        if (isEdit || loading) return;
        if (candidates.length === 1) {
            if (contractId !== candidates[0].id) setContractId(candidates[0].id);
        } else if (contractId && !candidates.some((contract) => contract.id === contractId)) {
            setContractId('');
        }
    }, [isEdit, loading, candidates, contractId, setContractId]);

    if (isEdit) {
        if (rootOptions.length > 1) {
            return (
                <ContractSelect
                    label="Vertrag (nur Root: Wechsel innerhalb derselben Agentur)"
                    name={name}
                    customerId={customerId}
                    filter={(contract) => rootOptions.some((option) => option.id === contract.id)}
                    disabled={disabled}
                />
            );
        }
        return contractId ? <ContractText contractId={contractId} /> : null;
    }

    if (loading) {
        return <Typography color="textSecondary">Vertrag wird ermittelt …</Typography>;
    }

    if (!agencyId) {
        return (
            <Alert severity="info">
                {NO_CARE_NEED_AGENCY_MESSAGE}{' '}
                <Link to={careNeedDashboardPath} onClick={handleOpenDashboard}>
                    Zum Einsatzboard
                </Link>
            </Alert>
        );
    }

    if (candidates.length === 0) {
        return (
            <Alert severity="warning">
                {`Kein vollständiger, laufender Vertrag mit ${agencyName(
                    agency
                )} vorhanden – BK-Einsatz kann nicht angelegt werden. Bitte zuerst den Vertrag anlegen bzw. vervollständigen.`}
            </Alert>
        );
    }

    if (candidates.length === 1) {
        return <ContractText contractId={candidates[0].id} />;
    }

    return (
        <ContractSelect
            label="Vertrag"
            name={name}
            customerId={customerId}
            filter={(contract) => candidates.some((candidate) => candidate.id === contract.id)}
            disabled={disabled}
        />
    );
};

DeploymentContractField.propTypes = {
    name: PropTypes.string.isRequired,
    customerId: IdPropType.isRequired,
    isEdit: PropTypes.bool,
    isRoot: PropTypes.bool,
    disabled: PropTypes.bool,
    onBlockedChange: PropTypes.func,
    onNavigateAway: PropTypes.func,
};

DeploymentContractField.defaultProps = {
    isEdit: false,
    isRoot: false,
    disabled: false,
    onBlockedChange: () => {},
    onNavigateAway: () => {},
};

export default DeploymentContractField;
